import type { ConsentRecord } from "~/composables/useConsent"

declare global {
    interface Window {
        dataLayer: unknown[]
        gtag: (...args: unknown[]) => void
    }
}

export default defineNuxtPlugin(() => {
    const config = useRuntimeConfig()
    const gtmId = (config.public as Record<string, string | undefined>).GTM_ID
    const gtagId = (config.public as Record<string, string | undefined>).GTAG_ID
    const adsId = (config.public as Record<string, string | undefined>).GOOGLE_ADS_ID

    window.dataLayer = window.dataLayer || []
    window.gtag = function gtag() {
        // eslint-disable-next-line prefer-rest-params
        window.dataLayer.push(arguments)
    }

    window.gtag("consent", "default", {
        ad_storage: "denied",
        analytics_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
        functionality_storage: "denied",
        personalization_storage: "denied",
        security_storage: "granted",
        wait_for_update: 500,
    })

    const { record, hasConsent } = useConsent()

    let gtmLoaded = false
    const loadGtm = () => {
        if (gtmLoaded || !gtmId) return
        gtmLoaded = true
        window.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" })
        const s = document.createElement("script")
        s.async = true
        s.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
        document.head.appendChild(s)
    }

    // gtag.js is a single shared library; GA4 and Google Ads both ride on it.
    // Load the <script> at most once, then add a `config` per product.
    let gtagScriptLoaded = false
    const ensureGtagScript = (id: string) => {
        if (gtagScriptLoaded) return
        gtagScriptLoaded = true
        const s = document.createElement("script")
        s.async = true
        s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`
        document.head.appendChild(s)
        window.gtag("js", new Date())
    }

    let gaLoaded = false
    const loadGa = () => {
        if (gaLoaded || !gtagId) return
        gaLoaded = true
        ensureGtagScript(gtagId)
        window.gtag("config", gtagId, { anonymize_ip: true })
    }

    // Google Ads conversion tracking / remarketing. Gated on `marketing`
    // consent because it sets ad_storage / ad_user_data / ad_personalization.
    let adsLoaded = false
    const loadAds = () => {
        if (adsLoaded || !adsId) return
        adsLoaded = true
        ensureGtagScript(adsId)
        window.gtag("config", adsId)
    }

    const applyFromRecord = (r: ConsentRecord | null) => {
        const functional = !!r?.choices.functional
        const analytics = !!r?.choices.analytics
        const marketing = !!r?.choices.marketing

        window.gtag("consent", "update", {
            ad_storage: marketing ? "granted" : "denied",
            analytics_storage: analytics ? "granted" : "denied",
            ad_user_data: marketing ? "granted" : "denied",
            ad_personalization: marketing ? "granted" : "denied",
            functionality_storage: functional ? "granted" : "denied",
            personalization_storage: functional ? "granted" : "denied",
        })

        if (analytics || marketing) loadGtm()
        if (analytics) loadGa()
        if (marketing) loadAds()
    }

    if (record.value) applyFromRecord(record.value)

    window.addEventListener("consent:update", ((e: CustomEvent<ConsentRecord>) => {
        applyFromRecord(e.detail)
    }) as EventListener)

    return {
        provide: {
            consentApplied: () => hasConsent("analytics") || hasConsent("marketing"),
        },
    }
})
